import { FC, useEffect, useMemo, useState, useCallback } from "react";
import AppModal from "../../../apps/components/AppModal";
import EvalStatus from "../../../apps/components/EvalStatus";
import { EvaluationStatusFilter, IEvaluatorProjects, IProjectEvaluatorData } from "../../../project/core/_models";
import { getEvaluatorProjects, sendEvaluationReminderEmails } from "../../../project/core/_project_evaluation";
import { useSnackbar } from "../../../../../_theme/context/SnackbarContext";
import { getApiErrorMessage } from "../../../../../utils/utils";

interface Props {
  show: boolean;
  onClose: () => void;
  selectedEvaluator: IProjectEvaluatorData;
  activeTab: EvaluationStatusFilter;
  setActiveTab: (tab: EvaluationStatusFilter) => void;
}

const TAB_CONFIG = [
  { key: EvaluationStatusFilter.all, title: "Assigned" },
  { key: EvaluationStatusFilter.completed, title: "Completed" },
  { key: EvaluationStatusFilter.pending, title: "Pending" },
  { key: EvaluationStatusFilter.overdue, title: "Overdue" },
] as const;

// Status mapping for consistent filtering
const STATUS_MAPPING = {
  [EvaluationStatusFilter.pending]: ['pending', 'in progress'],
  [EvaluationStatusFilter.overdue]: ['overdue'],
  [EvaluationStatusFilter.completed]: ['completed'],
  [EvaluationStatusFilter.all]: []
} as const;

const EvaluatorEvaluationModal: FC<Props> = ({
  show,
  onClose,
  selectedEvaluator,
  activeTab,
  setActiveTab
}) => {
  const { showSnackbar } = useSnackbar();
  const [selectedProjectIds, setSelectedProjectIds] = useState<number[]>([]);
  const [projects, setProjects] = useState<IEvaluatorProjects[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);

  // Filter projects based on active tab
  const filteredProjects = useMemo(() => {
    if (activeTab === EvaluationStatusFilter.all) {
      return projects;
    }

    const statuses = (STATUS_MAPPING[activeTab] || []) as unknown as string[];
    return projects.filter(project =>
      statuses.includes(project.eval_status?.toLowerCase() || '')
    );
  }, [projects, activeTab]);

  // Get selectable projects (excluding completed)
  const selectableProjects = useMemo(() => {
    return filteredProjects.filter(
      project => project.eval_status?.toLowerCase() !== 'completed'
    );
  }, [filteredProjects]);


  // Check if all selectable projects are selected
  const isAllSelected = useMemo(() => {
    return selectableProjects.length > 0 &&
      selectedProjectIds.length === selectableProjects.length;
  }, [selectedProjectIds, selectableProjects]);

  // Create Set for efficient lookup
  const selectedSet = useMemo(
    () => new Set(selectedProjectIds),
    [selectedProjectIds]
  );

  // Auto-select all selectable projects when:
  // 1. Tab changes
  // 2. Modal opens
  // 3. Projects are loaded
  useEffect(() => {
    if (selectableProjects.length > 0 && activeTab !== EvaluationStatusFilter.all) {
      setSelectedProjectIds(selectableProjects.map(p => p.project_id));
    } else {
      setSelectedProjectIds([]);
    }
  }, [selectableProjects]); // This will run when tab changes or projects are loaded

  // Fetch projects when modal opens
  useEffect(() => {
    if (show) {
      fetchEvaluatorProjects();
    }
  }, [show, selectedEvaluator.user_id]);

  const fetchEvaluatorProjects = async () => {
    try {
      setLoading(true);
      const response = await getEvaluatorProjects({
        evaluator_id: selectedEvaluator.user_id
      });

      if (response.status === 'ok') {
        setProjects(response.data);
      } else {
        showSnackbar(response.message, "danger");
      }
    } catch (error) {
      console.error("Failed to fetch projects:", error);
      showSnackbar(getApiErrorMessage(error, "Failed to fetch projects"), "danger");
    } finally {
      setLoading(false);
    }
  };

  const toggleSelectAll = useCallback((checked: boolean) => {
    if (checked) {
      // Select all non-completed projects
      setSelectedProjectIds(selectableProjects.map(p => p.project_id));
    } else {
      setSelectedProjectIds([]);
    }
  }, [selectableProjects]);

  const toggleProjectSelection = useCallback((projectId: number) => {
    setSelectedProjectIds(prev =>
      prev.includes(projectId)
        ? prev.filter(id => id !== projectId)
        : [...prev, projectId]
    );
  }, []);

  const handleTabChange = useCallback((tabKey: EvaluationStatusFilter) => {
    setActiveTab(tabKey);
  }, []);

  const handleSubmit = async () => {
    try {
      if (!selectedProjectIds.length) {
        showSnackbar("Please select at least one project", "danger");
        return;
      }

      setSubmitting(true);
      const response = await sendEvaluationReminderEmails({
        evaluator_id: selectedEvaluator.user_id,
        project_ids: selectedProjectIds
      });

      if (response.status === 'ok') {
        showSnackbar(response.message, "success");
        onClose();
      } else {
        showSnackbar(response.message, "danger");
      }
    } catch (error) {
      console.error("Failed to send evaluation reminder emails:", error);
      showSnackbar(getApiErrorMessage(error, "Failed to send evaluation reminder emails"), "danger");
    } finally {
      setSubmitting(false);
    }
  };

  const renderProjectItem = (project: IEvaluatorProjects) => {
    const isCompleted = project.eval_status?.toLowerCase() === 'completed';
    const isSelected = selectedSet.has(project.project_id);

    return (
      <div key={project.project_id} className="d-flex align-items-center justify-content-between py-3 border-bottom">
        <div className="d-flex align-items-center">
          <div className="form-check form-check-custom form-check-solid me-4">
            {!isCompleted ? (
              <input
                type="checkbox"
                className="form-check-input"
                checked={isSelected}
                onChange={() => toggleProjectSelection(project.project_id)}
                id={`project-${project.project_id}`}
              />
            ) : (
              <div style={{ width: "20px", height: "20px" }} />
            )}
          </div>
          <div>
            <div className="fw-bold">{project.project_name}</div>
            <div className="text-muted fs-7">
              Reminders Sent: {project.reminder_sent || 0}
            </div>
          </div>
        </div>

        <div className="d-flex align-items-center gap-4">
          <EvalStatus status={project.eval_status} />
        </div>
      </div>
    );
  };

  return (
    <AppModal
      show={show}
      title={`Send Reminder - ${selectedEvaluator.full_name}`}
      onClose={onClose}
      onSubmit={handleSubmit}
      submitText="Send Reminder"
      width="800px"
      showLoader={loading || submitting}
      loadingText={submitting ? "Sending reminders..." : "Loading projects..."}
      hideSubmitButton={!selectableProjects.length}
    >
      <div className="d-flex flex-column position-relative">
        {/* Header with Select All and Tabs */}
        <div className="d-flex align-items-center mb-5 justify-content-between">
          <div className="d-flex align-items-center gap-3">
            {selectableProjects.length > 0 && (
              <div className="form-check form-check-custom form-check-solid">
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={isAllSelected}
                  onChange={(e) => toggleSelectAll(e.target.checked)}
                  id="select-all"
                />
                <label className="form-check-label fw-semibold" htmlFor="select-all">
                  Select All
                </label>
              </div>
            )}
            <div className="fs-6 fw-semibold">
              {selectableProjects.length > 0 ? "(" : ""}{filteredProjects.length} projects found{selectableProjects.length > 0 ? ") " : ""}
            </div>
          </div>

          {/* Tabs */}
          <ul className="nav nav-pills nav-pills-custom flex-nowrap overflow-auto gap-3 bg-light rounded-pill p-2">
            {TAB_CONFIG.map((tab) => (
              <li key={tab.key} className="nav-item me-0">
                <button
                  type="button"
                  onClick={() => handleTabChange(tab.key)}
                  className={`nav-link fs-7 px-4 py-2 rounded-pill border-0 
                    ${activeTab === tab.key
                      ? "active text-white bg-primary shadow-sm"
                      : "bg-gray-200 text-gray-700"
                    }`}
                >
                  {tab.title}
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Projects List */}
        <div style={{ maxHeight: "400px", overflowY: "auto" }} className="border rounded p-3">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : filteredProjects.length > 0 ? (
            filteredProjects.map(renderProjectItem)
          ) : (
            <div className="text-center py-5 text-muted">
              No projects found
            </div>
          )}
        </div>

        {/* Selection Summary */}
        {selectedProjectIds.length > 0 && (
          <div className="mt-3 text-muted fs-7">
            {selectedProjectIds.length} project{selectedProjectIds.length !== 1 ? 's' : ''} selected
          </div>
        )}
      </div>
    </AppModal>
  );
};

export default EvaluatorEvaluationModal;